import addDays from 'date-fns/addDays';

import { DateFormat, formatDate, formatDateWithYear } from '@lyearn/core/utils/date/formatDate';
import { TFunction } from '@lyearn/i18n';
import { ActionMenuProps } from '@lyearn/molecules';
import {
  AssignmentDueDateEnum,
  AssignmentItemDueDateConfig,
  AssignmentItemDueDateConfigInput,
  AssignmentItemTypeEnum,
  AssignmentScheduleDateEnum,
  DueDateAssignmentConfig,
  RelativeDateFilterOperatorEnum,
  RelativeDateFilterUnitEnum,
  ScheduleDateAssignmentConfigInput,
} from '@/types/schema';

import {
  AdaptedNestedDueDateConfig,
  InViewItemForDueDate,
} from '../components/ContentAndCollectionList/hooks/useContentAndCollectionList';
import { AdaptedAssignmentItemDueDates } from '../types';

export enum ScheduleForEnum {
  TomorrowAt9 = 'TOMMOROW_AT_9',
  MondayAt9 = 'MONDAY_AT_9',
  After7Days = 'AFTER_7_Days',
  Custom = 'CUSTOM',
  NoScheduleDate = 'NO_SCHEDULE_DATE',
}

export type ScheduleItem = {
  id: ScheduleForEnum;
  label: string;
  value: ScheduleDateAssignmentConfigInput;
};

export const getCustomOption = (t: TFunction) => ({
  id: ScheduleForEnum.Custom,
  label: t('Common:Custom'),
  value: {
    scheduleDateType: AssignmentScheduleDateEnum.Fixed,
  },
});

export const NO_SCHEDULE_DATE: ScheduleItem = {
  id: ScheduleForEnum.NoScheduleDate,
  label: '',
  value: {
    scheduleDateType: AssignmentScheduleDateEnum.NoScheduleDate,
  },
};

export const getScheduledForOption = (t: TFunction): ScheduleItem[] => [
  {
    id: ScheduleForEnum.TomorrowAt9,
    label: t('AssignmentCreator:TomorrowAt9'),
    value: {
      scheduleDateType: AssignmentScheduleDateEnum.Fixed,
    },
  },
  {
    id: ScheduleForEnum.MondayAt9,
    label: t('AssignmentCreator:MondayAt9'),
    value: {
      scheduleDateType: AssignmentScheduleDateEnum.Fixed,
    },
  },
  {
    id: ScheduleForEnum.After7Days,
    label: t('AssignmentCreator:After7Days'),
    value: {
      scheduleDateType: AssignmentScheduleDateEnum.Dynamic,
      value: 7,
      operator: RelativeDateFilterOperatorEnum.Next,
      unit: RelativeDateFilterUnitEnum.Day,
    },
  },
  getCustomOption(t),
];

export const getScheduledDate = (scheduleDateConfig: ScheduleDateAssignmentConfigInput) => {
  if (scheduleDateConfig.scheduleDate) {
    return formatDate(scheduleDateConfig.scheduleDate, DateFormat.DateTime);
  }
  if (
    scheduleDateConfig.scheduleDateType === AssignmentScheduleDateEnum.Dynamic &&
    scheduleDateConfig.value
  ) {
    return formatDate(addDays(new Date(), scheduleDateConfig.value), DateFormat.DateTime);
  }
  return undefined;
};

export const getPathMenuItems = (t: TFunction): ActionMenuProps['items'] => [
  { type: 'label', label: t('AssignmentCreator:OverrideDueDate') },
  {
    id: 'path',
    label: t('Common:ResourceType.Path'),
  },
  {
    id: 'section',
    label: t('AssignmentCreator:Section'),
  },
  {
    id: 'content',
    label: t('AssignmentCreator:Content'),
  },
];

export const getCollectionMenuItems = (t: TFunction): ActionMenuProps['items'] => [
  { type: 'label', label: t('AssignmentCreator:OverrideDueDate') },
  {
    id: 'collection',
    label: t('AssignmentCreator:Collection'),
  },
  {
    id: 'content',
    label: t('AssignmentCreator:Content'),
  },
];

const adaptNestedItem = (item: AssignmentItemDueDateConfig) => {
  if (item.nestedDueDateConfig?.length) {
    const adaptedNestedData: AdaptedNestedDueDateConfig = {};
    item.nestedDueDateConfig.forEach((val) => {
      adaptedNestedData[val.itemId] = val;
    });

    return {
      ...item,
      nestedDueDateConfig: adaptedNestedData,
    };
  }

  return {
    ...item,
    nestedDueDateConfig: {},
  };
};

export const adaptDueDatesOnEdit = (
  assignmentItemsDueDateConfig?: AssignmentItemDueDateConfig[] | null,
) => {
  const dueDateMap: AdaptedAssignmentItemDueDates = {};
  if (!assignmentItemsDueDateConfig) {
    return dueDateMap;
  }

  assignmentItemsDueDateConfig.forEach((item) => {
    dueDateMap[item.itemId] = adaptNestedItem(item);
  });

  return dueDateMap;
};

export const adaptDueDatesOnSubmit = (
  dueDates: AdaptedAssignmentItemDueDates,
): AssignmentItemDueDateConfigInput[] | null | undefined => {
  const adaptedDueDateValues = Object.values(dueDates)
    .filter((item) => item)
    .map((item) => {
      return {
        ...item,
        nestedDueDateConfig: item?.nestedDueDateConfig
          ? Object.values(item.nestedDueDateConfig).filter((val) => val)
          : undefined,
      };
    });

  return adaptedDueDateValues as AssignmentItemDueDateConfigInput[] | null | undefined;
};

export const getSelectedDueDateLabel = (
  t: TFunction,
  selectedDueDate?: DueDateAssignmentConfig | null,
) => {
  if (selectedDueDate) {
    const { dueDateType, dueDate, value } = selectedDueDate;
    switch (dueDateType) {
      case AssignmentDueDateEnum.Dynamic:
        return value ? t('AssignmentCreator:DaysCount', { count: value }) : undefined;
      case AssignmentDueDateEnum.NoDueDate:
        return t('AssignmentCreator:NeverDue');
      case AssignmentDueDateEnum.Fixed:
        return dueDate ? formatDateWithYear(dueDate, DateFormat.DateTime) : undefined;
      case AssignmentDueDateEnum.Relative:
        return t('AssignmentCreator:OnCondition');
    }
  }
};

export const getSelectedDueDate = (
  inViewItemForDueDate: InViewItemForDueDate,
  assignmentItemsDueDateConfig: AdaptedAssignmentItemDueDates,
) => {
  const { itemType } = inViewItemForDueDate;
  let itemId: string | undefined;
  switch (itemType) {
    case AssignmentItemTypeEnum.Content:
      itemId = inViewItemForDueDate.editingContent?.id ?? inViewItemForDueDate.editingSession?.id;
      break;
    case AssignmentItemTypeEnum.Collection:
      itemId = inViewItemForDueDate.editingCollection?.id;
      break;
    case AssignmentItemTypeEnum.Section:
      itemId = inViewItemForDueDate.editingSection?.id;
      break;
    default:
  }

  if (itemId) {
    return { itemId, itemType, dueDateConfig: assignmentItemsDueDateConfig[itemId]?.dueDateConfig };
  }
};

export const getAssignmentNumberLabel = (assignmentNumber?: number) =>
  typeof assignmentNumber === 'number' ? `#${assignmentNumber}` : '';
